import { useCallback, useEffect, useState } from 'react';
import {
  biopsApi,
  type BIRecommendation,
  type BIOpsDashboard,
  type BrandAnalyticsRecord,
  type BusinessMetricRecord,
  type ExecutiveInsightRecord,
  type ExecutiveScorecard,
  type ForecastRecord,
  type SearchMetricRecord,
} from '../../api/biops';

type Tab = 'dashboard' | 'business' | 'brand' | 'search' | 'insights' | 'forecasts' | 'scorecard' | 'recommendations';

function trendClass(t?: string) {
  if (t === 'up') return 'text-emerald-700';
  if (t === 'down') return 'text-red-700';
  return 'text-slate-600';
}

function severityClass(s?: string) {
  if (s === 'positive') return 'text-emerald-700 bg-emerald-50';
  if (s === 'critical' || s === 'negative') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function scoreClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

function fmtCurrency(n: number) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export function BIOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<BIOpsDashboard | null>(null);
  const [business, setBusiness] = useState<BusinessMetricRecord[]>([]);
  const [brand, setBrand] = useState<BrandAnalyticsRecord[]>([]);
  const [search, setSearch] = useState<SearchMetricRecord[]>([]);
  const [insights, setInsights] = useState<ExecutiveInsightRecord[]>([]);
  const [forecasts, setForecasts] = useState<ForecastRecord[]>([]);
  const [scorecard, setScorecard] = useState<ExecutiveScorecard | null>(null);
  const [recommendations, setRecommendations] = useState<BIRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, biz, br, srch, ins, fcst, sc, recs] = await Promise.all([
        biopsApi.dashboard(), biopsApi.listBusiness(), biopsApi.listBrandAnalytics(),
        biopsApi.listSearch(), biopsApi.listInsights(), biopsApi.listForecasts(),
        biopsApi.getScorecard(), biopsApi.listRecommendations(),
      ]);
      setDashboard(dash); setBusiness(biz); setBrand(br); setSearch(srch);
      setInsights(ins); setForecasts(fcst); setScorecard(sc); setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load business intelligence data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await biopsApi.generateRecommendations(); await refresh(); } finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Executive Console' },
    { id: 'business', label: 'Business Overview' },
    { id: 'brand', label: 'Brand Analytics' },
    { id: 'search', label: 'Search Explorer' },
    { id: 'insights', label: 'Executive Insights' },
    { id: 'forecasts', label: 'Forecast Center' },
    { id: 'scorecard', label: 'Scorecard' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Business Intelligence &amp; Executive Insights</h1>
          <p className="text-sm text-slate-500">Brand analytics, search performance, forecasts &amp; AI business copilot — APP-00008</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">Refresh</button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total Sales', value: fmtCurrency(dashboard.total_sales) },
            { label: 'Sales Growth', value: `${dashboard.sales_growth_pct >= 0 ? '+' : ''}${dashboard.sales_growth_pct.toFixed(1)}%` },
            { label: 'Sessions', value: dashboard.total_sessions.toLocaleString() },
            { label: 'Conversion', value: `${(dashboard.avg_conversion_rate * 100).toFixed(1)}%` },
            { label: 'Buy Box', value: `${dashboard.avg_buy_box_pct.toFixed(1)}%` },
            { label: 'Brand Share', value: `${(dashboard.brand_share_avg * 100).toFixed(1)}%` },
            { label: 'Business Score', value: dashboard.overall_business_score.toFixed(0) },
            { label: 'Active Insights', value: dashboard.active_insights.toString() },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'business' && (
        <div className="space-y-3">
          {business.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{b.period?.replace(/_/g, ' ')}</span>
                <span className={`text-lg font-semibold ${trendClass(b.sales_trend)}`}>{fmtCurrency(b.sales)}</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-4 text-sm text-slate-600">
                <span>{b.units_ordered.toLocaleString()} units</span>
                <span>{b.sessions.toLocaleString()} sessions</span>
                <span>{(b.conversion_rate * 100).toFixed(1)}% conv</span>
                <span>{b.buy_box_pct.toFixed(1)}% buy box</span>
                <span>Organic: {fmtCurrency(b.organic_sales)}</span>
                <span>Sponsored: {fmtCurrency(b.sponsored_sales)}</span>
                <span>AOV: ${b.avg_order_value.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'brand' && (
        <div className="space-y-3">
          {brand.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{b.search_term}</span>
                <span className="text-xs text-slate-400">SFR #{b.search_frequency_rank.toLocaleString()}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Click: {(b.click_share * 100).toFixed(0)}%</span>
                <span>Conv: {(b.conversion_share * 100).toFixed(0)}%</span>
                <span>Brand: {(b.brand_share * 100).toFixed(0)}%</span>
                <span>Category: {(b.category_share * 100).toFixed(0)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'search' && (
        <div className="space-y-3">
          {search.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{s.keyword}</span>
                <span className="font-mono text-sm">#{s.current_rank}</span>
                {s.rank_change !== 0 && (
                  <span className={`text-sm font-medium ${s.rank_change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {s.rank_change > 0 ? '+' : ''}{s.rank_change}
                  </span>
                )}
                <span className={`text-xs ${trendClass(s.trend)}`}>{s.trend}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                <span>Demand: {(s.search_demand * 100).toFixed(0)}%</span>
                <span>Organic: {(s.organic_share * 100).toFixed(0)}%</span>
                <span>Sponsored: {(s.sponsored_influence * 100).toFixed(0)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'insights' && (
        <div className="space-y-3">
          {insights.map((i) => (
            <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(i.severity)}`}>{i.type.replace(/_/g, ' ')}</span>
                {i.impact_pct != null && <span className="text-sm font-medium">{i.impact_pct.toFixed(1)}% impact</span>}
              </div>
              <h3 className="mt-1 font-medium text-slate-900">{i.title}</h3>
              {i.summary && <p className="mt-1 text-sm text-slate-600">{i.summary}</p>}
              {i.evidence && <p className="mt-1 text-xs text-slate-400">{i.evidence}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'forecasts' && (
        <div className="space-y-3">
          {forecasts.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{f.metric?.replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-400">{f.horizon_days}-day horizon</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3 text-sm text-slate-600">
                <span>Revenue: {fmtCurrency(f.forecast_revenue)}</span>
                <span>Orders: {f.forecast_orders.toLocaleString()}</span>
                <span>Profit: {fmtCurrency(f.forecast_profit)}</span>
                <span>Traffic: {f.forecast_traffic.toLocaleString()}</span>
                <span>Conv: {(f.forecast_conversion * 100).toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'scorecard' && scorecard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Revenue', score: scorecard.revenue_score },
            { label: 'Growth', score: scorecard.growth_score },
            { label: 'Profit', score: scorecard.profit_score },
            { label: 'Traffic', score: scorecard.traffic_score },
            { label: 'Brand', score: scorecard.brand_score },
            { label: 'Customer', score: scorecard.customer_score },
            { label: 'Operational', score: scorecard.operational_score },
            { label: 'Overall', score: scorecard.overall_business_score },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{s.label}</p>
              <p className={`mt-1 text-2xl font-semibold ${scoreClass(s.score)}`}>{s.score.toFixed(0)}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">#{r.priority_rank}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
